import type { ConsentChannel } from './clients.js'

/** The three delivery channels. Mirrors the per-client consent flags. */
export const MESSAGE_CHANNELS = ['sms', 'whatsapp', 'email'] as const
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number]

export const MESSAGE_CHANNEL_LABELS: Record<MessageChannel, string> = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'Email',
}

/** A channel maps 1:1 to the consent flag it must respect (the choke point checks this). */
export const CHANNEL_CONSENT_FIELD: Record<MessageChannel, ConsentChannel> = {
  sms: 'sms',
  whatsapp: 'whatsapp',
  email: 'email',
}

/** Why a message was sent — drives frequency-cap scope and the client's history view. */
export const MESSAGE_REF_TYPES = ['birthday', 'campaign', 'welcome', 'manual'] as const
export type MessageRefType = (typeof MESSAGE_REF_TYPES)[number]

export const MESSAGE_REF_TYPE_LABELS: Record<MessageRefType, string> = {
  birthday: 'Birthday greeting',
  campaign: 'Campaign',
  welcome: 'Welcome message',
  manual: 'Manual message',
}

/**
 * Transactional sends bypass the marketing frequency cap. A welcome on
 * confirmation and a one-off staff message are responses to something the
 * client did, not marketing blasts — capping them would drop legitimate mail.
 * Consent is still enforced for all of them.
 */
export const MARKETING_REF_TYPES: readonly MessageRefType[] = ['birthday', 'campaign']

/** Outbox lifecycle. `dead` is terminal after N failed attempts. */
export const OUTBOX_STATUSES = ['queued', 'sending', 'sent', 'failed', 'dead', 'skipped'] as const
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number]

/** Per-recipient status inside a campaign — a projection of the outbox row. */
export const RECIPIENT_STATUSES = ['queued', 'sent', 'failed', 'skipped'] as const
export type RecipientStatus = (typeof RECIPIENT_STATUSES)[number]

export const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'sending', 'done', 'cancelled'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  sending: 'Sending',
  done: 'Done',
  cancelled: 'Cancelled',
}

/** Which templates the system seeds and relies on; they can be edited but not deleted. */
export const SYSTEM_TEMPLATE_KINDS = ['birthday', 'welcome'] as const
export type SystemTemplateKind = (typeof SYSTEM_TEMPLATE_KINDS)[number]

/**
 * Placeholders a template body may contain, resolved per client at send time.
 * The set is closed: an unknown `{{token}}` is a template mistake, and rendering
 * leaves it visible rather than substituting something surprising.
 */
export const TEMPLATE_PLACEHOLDERS = [
  'first_name',
  'full_name',
  'preferred_country',
  'agency_name',
] as const
export type TemplatePlaceholder = (typeof TEMPLATE_PLACEHOLDERS)[number]

export const PLACEHOLDER_DESCRIPTIONS: Record<TemplatePlaceholder, string> = {
  first_name: 'Client’s first name',
  full_name: 'Client’s full name',
  preferred_country: 'First preferred country (or a neutral fallback)',
  agency_name: 'The agency’s name',
}

/** Provider result shape shared across adapters. */
export interface ProviderSendResult {
  ok: boolean
  providerMessageId?: string
  error?: string
}
